<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import {
  searchContacts,
  createNewContact,
  findOrCreateContact,
  fetchContactableInboxes,
  mergeInboxDetails,
  hasDraftContent,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import ContactAPI from 'dashboard/api/contacts';
import wootConstants from 'dashboard/constants/globals';

import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const contacts = ref([]);
const selectedContacts = ref([]);
const primaryContactDetails = ref(null);
const targetInbox = ref(null);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const showComposeNewConversation = ref(false);
// True while a nested overlay (the AI assist dropdown or its modal) is open, so Escape closes that
// rather than discarding the whole compose form.
const hasActiveOverlay = ref(false);

const currentDraftId = ref(null);
// Conversation the compose modal was opened from, when forwarding a message.
const sourceConversationId = ref(null);

const currentFormState = ref({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
});

const draftState = ref({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  toEmails: [],
  selectedContactId: null,
  targetInboxId: null,
  fromEmail: null,
});

const draftFiles = computed(() => {
  if (!currentDraftId.value) return [];
  const draft = store.getters['messageDrafts/getDraft'](currentDraftId.value);
  return draft?.publicFiles || [];
});

const uploadingFiles = computed(() => {
  if (!currentDraftId.value) return [];
  return store.getters['messageDrafts/getUploadingFiles'](currentDraftId.value);
});

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const currentAccount = useMapGetter('accounts/getCurrentAccount');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxesWithInternal');
const signatureEnabled = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const resetContacts = () => {
  contacts.value = [];
};

// Set while the contact of a draft with a saved inbox loads: the draft then picks its own
// inbox, and when that inbox is no longer offered (e.g. marked for deletion) the agent picks
// the sender, instead of the only remaining inbox being swapped in.
const restoringDraftInboxId = ref(null);

const handleTargetInbox = inbox => {
  targetInbox.value = inbox;
  resetContacts();

  if (
    inbox?.channelType !== 'Channel::Email' &&
    selectedContacts.value.length > 1
  ) {
    selectedContacts.value = [selectedContacts.value[0]];
  }
};

const fetchAndSetContactInboxes = async contactId => {
  isFetchingInboxes.value = true;
  try {
    const contactableInboxes = await fetchContactableInboxes(contactId);
    const mergedInboxes = mergeInboxDetails(
      contactableInboxes,
      inboxesList.value
    );
    selectedContacts.value = selectedContacts.value.map((c, i) =>
      i === 0 ? { ...c, contactInboxes: mergedInboxes } : c
    );

    if (
      !targetInbox.value &&
      !restoringDraftInboxId.value &&
      mergedInboxes.length === 1
    ) {
      handleTargetInbox(mergedInboxes[0]);
    }
  } finally {
    isFetchingInboxes.value = false;
  }
};

const loadPrimaryContactDetails = async id => {
  if (!id) {
    primaryContactDetails.value = null;
    return;
  }
  try {
    const {
      data: { payload },
    } = await ContactAPI.show(id);
    primaryContactDetails.value = payload;
  } catch (error) {
    primaryContactDetails.value = null;
  }
};

const handleAddContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
      isCreatingContact.value = false;
    } catch (error) {
      isCreatingContact.value = false;
      return;
    }
  } else {
    contact = rest;
  }

  if (
    contact?.email &&
    selectedContacts.value.some(c => c.email === contact.email)
  ) {
    return;
  }

  const isFirstContact = selectedContacts.value.length === 0;
  selectedContacts.value = [...selectedContacts.value, contact];

  if (isFirstContact && contact?.id) {
    loadPrimaryContactDetails(contact.id);
    await fetchAndSetContactInboxes(contact.id);
  }
};

const handleRemoveContact = async index => {
  const wasFirst = index === 0;
  const newContacts = [...selectedContacts.value];
  newContacts.splice(index, 1);
  selectedContacts.value = newContacts;

  if (selectedContacts.value.length === 0) {
    targetInbox.value = null;
    primaryContactDetails.value = null;
    return;
  }

  if (wasFirst) {
    const newFirst = selectedContacts.value[0];
    if (newFirst?.id) {
      loadPrimaryContactDetails(newFirst.id);
      await fetchAndSetContactInboxes(newFirst.id);
    } else {
      primaryContactDetails.value = null;
    }
  }
};

let pendingCreatePromise = null;
const ensureDraftExists = async () => {
  if (currentDraftId.value) return currentDraftId.value;

  if (!pendingCreatePromise) {
    pendingCreatePromise = store
      .dispatch('messageDrafts/create', {})
      .then(draft => {
        currentDraftId.value = draft.id;
        pendingCreatePromise = null;
        return draft.id;
      })
      .catch(err => {
        pendingCreatePromise = null;
        throw err;
      });
  }

  return pendingCreatePromise;
};

const applyDraft = async draft => {
  if (!draft) return;

  currentDraftId.value = draft.id;

  // Set draft state
  draftState.value = {
    message: draft.publicContent || '',
    subject: draft.subject || '',
    ccEmails: (draft.ccEmails || []).join(', '),
    bccEmails: (draft.bccEmails || []).join(', '),
    toEmails: draft.toEmails || [],
    selectedContactId: draft.contactId,
    targetInboxId: draft.inboxId,
    fromEmail: draft.fromEmail || null,
  };

  // Load contact if exists
  if (draft.contactId) {
    let contact = contactById.value(draft.contactId);

    // Try with numeric ID if string ID doesn't work
    if (!contact || !contact.id) {
      const numericId = parseInt(draft.contactId, 10);
      contact = contactById.value(numericId);
    }

    // If contact is not in store (empty object or no id), fetch it using store action
    if (!contact || !contact.id) {
      try {
        await store.dispatch('contacts/show', { id: draft.contactId });
        contact = contactById.value(draft.contactId);
      } catch (error) {
        return;
      }
    }

    if (contact && contact.id) {
      restoringDraftInboxId.value = draft.inboxId || null;
      try {
        await handleAddContact({
          action: 'select',
          ...contact,
        });
      } finally {
        restoringDraftInboxId.value = null;
      }

      if (draft.toEmails && draft.toEmails.length > 0) {
        const firstContact = selectedContacts.value[0];
        const additionalContacts = draft.toEmails.map(email => ({
          email,
          name: email,
        }));
        selectedContacts.value = [firstContact, ...additionalContacts];
      }

      // Load inbox after contact is loaded
      if (draft.inboxId && selectedContacts.value.length > 0) {
        setTimeout(() => {
          const contactInboxes =
            selectedContacts.value[0]?.contactInboxes || [];

          // Try both structures - direct inbox object or nested inbox
          let inbox = contactInboxes.find(ci => ci.id === draft.inboxId);
          if (!inbox) {
            inbox = contactInboxes.find(
              ci => ci.inbox?.id === draft.inboxId
            )?.inbox;
          }

          if (inbox) {
            const variant = draft.fromEmail
              ? {
                  ...inbox,
                  email: draft.fromEmail,
                  senderEmail: draft.fromEmail,
                }
              : inbox;
            handleTargetInbox(variant);
          }
        }, 100);
      }
    }
  }
};

const onContactSearch = debounce(
  async query => {
    // only search if query has 3 or more characters
    if (query.query.length < 3) {
      return;
    }

    isSearching.value = true;
    contacts.value = [];
    try {
      contacts.value = await searchContacts(query);
      isSearching.value = false;
    } catch (error) {
      useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    } finally {
      isSearching.value = false;
    }
  },
  100,
  false
);

const clearAllContacts = () => {
  selectedContacts.value = [];
  primaryContactDetails.value = null;
  targetInbox.value = null;
};

const resetDraftState = () => {
  currentDraftId.value = null;
  sourceConversationId.value = null;
  draftState.value = {
    message: '',
    subject: '',
    ccEmails: '',
    bccEmails: '',
    toEmails: [],
    selectedContactId: null,
    targetInboxId: null,
    fromEmail: null,
  };
};

const closeCompose = async () => {
  showComposeNewConversation.value = false;
  selectedContacts.value = [];
  primaryContactDetails.value = null;
  targetInbox.value = null;

  if (currentDraftId.value) {
    await store.dispatch('messageDrafts/destroy', currentDraftId.value);
  }
  resetDraftState();
  resetContacts();
  emit('close');
};

const buildDraftParams = () => {
  const ccEmails = currentFormState.value.ccEmails
    ? currentFormState.value.ccEmails
        .split(',')
        .map(e => e.trim())
        .filter(Boolean)
    : [];
  const bccEmails = currentFormState.value.bccEmails
    ? currentFormState.value.bccEmails
        .split(',')
        .map(e => e.trim())
        .filter(Boolean)
    : [];
  const toEmails = selectedContacts.value
    .slice(1)
    .map(c => c.email)
    .filter(Boolean);
  return {
    publicContent: currentFormState.value.message || '',
    subject: currentFormState.value.subject || '',
    ccEmails,
    bccEmails,
    toEmails,
    contactId: selectedContacts.value[0]?.id || null,
    inboxId: targetInbox.value?.id || null,
    fromEmail: targetInbox.value?.senderEmail || null,
  };
};

const isDraftEmpty = params => {
  return !hasDraftContent({
    message: params.publicContent,
    subject: params.subject,
    ccEmails: (params.ccEmails || []).join(', '),
    bccEmails: (params.bccEmails || []).join(', '),
    files: draftFiles.value,
    uploadingFiles: uploadingFiles.value,
  });
};

const handleSaveDraft = async () => {
  const params = buildDraftParams();

  if (isDraftEmpty(params)) {
    if (currentDraftId.value) {
      await store.dispatch('messageDrafts/destroy', currentDraftId.value);
      resetDraftState();
    }
    showComposeNewConversation.value = false;
    emit('close');
    return;
  }

  if (currentDraftId.value) {
    await store.dispatch('messageDrafts/update', {
      draftId: currentDraftId.value,
      ...params,
    });
  } else {
    const draft = await store.dispatch('messageDrafts/create', params);
    currentDraftId.value = draft.id;
  }

  showComposeNewConversation.value = false;
  emit('close');
};

// Update current form state when form changes
const handleFormStateChange = formState => {
  currentFormState.value = { ...formState };
};

const handleAttachFile = async ({ blob, file }) => {
  if (!file) return;
  const draftId = await ensureDraftExists();
  const fileToUpload = blob ? blob.signed_id : file.file;
  store.dispatch('messageDrafts/addAttachment', {
    draftId,
    file: fileToUpload,
    fileMeta: { name: file.name, size: file.size, type: file.type },
    isPrivate: false,
  });
};

const handleRemoveDraftAttachment = attachmentId => {
  if (!currentDraftId.value) return;
  store.dispatch('messageDrafts/removeAttachment', {
    draftId: currentDraftId.value,
    attachmentId,
  });
};

const handleCancelUpload = tempId => {
  if (!currentDraftId.value) return;
  store.dispatch('messageDrafts/cancelUpload', {
    draftId: currentDraftId.value,
    tempId,
  });
};

const handleAppendCannedAttachments = async cannedFiles => {
  const draftId = await ensureDraftExists();

  cannedFiles.forEach(file => {
    store.dispatch('messageDrafts/addAttachment', {
      draftId,
      file: file.blob_signed_id,
      fileMeta: {
        name: file.filename,
        size: file.byte_size,
        type: file.file_type,
      },
      isPrivate: false,
    });
  });
};

const createConversation = async ({
  payload,
  isFromWhatsApp,
  resolveSourceConversation = false,
}) => {
  const originalConversationId = sourceConversationId.value;
  try {
    const params = { ...payload };
    if (currentDraftId.value) {
      params.messageDraftId = currentDraftId.value;
    }
    const data = await store.dispatch('contactConversations/create', {
      params,
      isFromWhatsApp,
    });
    if (resolveSourceConversation && originalConversationId) {
      // The new conversation already exists; a failed resolve must not
      // report the whole compose as failed
      await store
        .dispatch('toggleStatus', {
          conversationId: originalConversationId,
          status: wootConstants.STATUS_TYPE.RESOLVED,
        })
        .catch(() => useAlert(t('CONVERSATION.CHANGE_STATUS_FAILED')));
    }
    const action = {
      type: 'link',
      to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
      message: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
    };
    if (currentDraftId.value) {
      store.commit('messageDrafts/REMOVE_NEW_CONV_DRAFT', {
        draftId: currentDraftId.value,
      });
    }
    resetDraftState();
    showComposeNewConversation.value = false;
    selectedContacts.value = [];
    primaryContactDetails.value = null;
    targetInbox.value = null;
    resetContacts();
    emit('close');
    useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
    return true;
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
    return false;
  }
};

const openWithDraft = async (draftId, conversationId = null) => {
  resetDraftState();
  clearAllContacts();
  showComposeNewConversation.value = true;
  sourceConversationId.value = conversationId;

  if (draftId) {
    const draft = store.getters['messageDrafts/getDraft'](draftId);
    if (draft) {
      await applyDraft(draft);
    }
  }
};

const handleClickOutside = async () => {
  if (!showComposeNewConversation.value) return;

  showComposeNewConversation.value = false;
  await handleSaveDraft();
  emit('close');
};

const onModalBackdropClick = () => {
  handleClickOutside();
};

onMounted(async () => {
  resetContacts();
});

const keyboardEvents = {
  Escape: {
    action: () => {
      if (hasActiveOverlay.value) return;
      if (showComposeNewConversation.value) {
        showComposeNewConversation.value = false;
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);

// Seeds the modal from a raw email address (deep links from the Chrome
// extension / /app/compose). The address is resolved to a contact so the
// inbox selector can populate; find_or_create dedupes server side.
const addContactByEmail = async email => {
  isCreatingContact.value = true;
  try {
    const contact = await findOrCreateContact(email);
    if (contact?.id) {
      await handleAddContact({ action: 'select', ...contact });
    }
  } catch (error) {
    useAlert(t('COMPOSE_REDIRECT.CONTACT_ERROR', { email }));
  } finally {
    isCreatingContact.value = false;
  }
};

const openWithContext = async ({ contactId, email } = {}) => {
  resetDraftState();
  clearAllContacts();

  if (contactId) {
    let contact = contactById.value(contactId);
    if (!contact || !contact.id) {
      const numericId = parseInt(contactId, 10);
      contact = contactById.value(numericId);
    }
    if (!contact || !contact.id) {
      await store.dispatch('contacts/show', { id: contactId });
      contact = contactById.value(contactId);
    }
    if (contact && contact.id) {
      await handleAddContact({ action: 'select', ...contact });
    }
    showComposeNewConversation.value = true;
    return;
  }

  // Show the modal before the lookup round trip so the agent sees it open
  // immediately; the contact chip fills in behind the selector's spinner.
  showComposeNewConversation.value = true;

  if (email) {
    await addContactByEmail(email);
  }
};

defineExpose({
  openWithDraft,
  openWithContext,
});
</script>

<template>
  <!-- Teleported: on phones the sidebar drawer has a transform, which makes
       it the containing block of this fixed overlay and squeezes the modal -->
  <TeleportWithDirection>
    <div
      v-if="showComposeNewConversation"
      class="fixed z-50 bg-modal-backdrop-light dark:bg-modal-backdrop-dark flex items-start pt-[clamp(1rem,5vh,6rem)] max-md:pt-0 justify-center inset-0"
      @mousedown.self="onModalBackdropClick"
    >
      <ComposeNewConversationForm
        :contacts="contacts"
        :is-loading="isSearching"
        :current-user="currentUser"
        :current-account="currentAccount"
        :selected-contacts="selectedContacts"
        :primary-contact-details="primaryContactDetails"
        :target-inbox="targetInbox"
        :is-creating-contact="isCreatingContact"
        :is-fetching-inboxes="isFetchingInboxes"
        :contact-conversations-ui-flags="uiFlags"
        :contacts-ui-flags="contactsUiFlags"
        :message-signature="messageSignature"
        :signature-enabled="signatureEnabled"
        :draft-message="draftState.message"
        :draft-subject="draftState.subject"
        :draft-cc-emails="draftState.ccEmails"
        :draft-bcc-emails="draftState.bccEmails"
        :draft-files="draftFiles"
        :uploading-files="uploadingFiles"
        :source-conversation-id="sourceConversationId"
        @overlay-toggle="hasActiveOverlay = $event"
        @search-contacts="onContactSearch"
        @reset-contact-search="resetContacts"
        @add-contact="handleAddContact"
        @remove-contact="handleRemoveContact"
        @update-target-inbox="handleTargetInbox"
        @create-conversation="createConversation"
        @discard="closeCompose"
        @save-draft="handleSaveDraft"
        @form-state-change="handleFormStateChange"
        @attach-file="handleAttachFile"
        @remove-draft-attachment="handleRemoveDraftAttachment"
        @cancel-upload="handleCancelUpload"
        @append-canned-attachments="handleAppendCannedAttachments"
      />
    </div>
  </TeleportWithDirection>
</template>
